import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const BrokersTab = ({
  adminBrokers,
  brokerInstrumentDrafts,
  brokerInstrumentInputs,
  setBrokerInstrumentInputs,
  savingBrokerId,
  marketSessionConfigDraft,
  setMarketSessionConfigDraft,
  savingMarketSessionConfig,
  handleToggleBrokerActive,
  handleSaveBroker,
  addBrokerInstrument,
  removeBrokerInstrument,
  updateBrokerInstrumentField,
  handleSaveMarketSessionConfig,
}) => {
  return (
    <div className="space-y-6">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Broker Instrument Registry</CardTitle>
          <CardDescription>
            Manage the instrument catalog each broker exposes to the app, including optional symbol and contract metadata.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {adminBrokers.length === 0 ? (
            <div className="text-sm text-zinc-500">No broker configurations found.</div>
          ) : (
            <div className="space-y-4">
              {adminBrokers.map((broker) => {
                const draft = brokerInstrumentDrafts[broker.id] || [];
                return (
                  <div key={broker.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <div className="font-semibold text-white">{broker.display_name || broker.name}</div>
                          <Badge variant={broker.is_active ? 'default' : 'secondary'}>
                            {broker.is_active ? 'Active' : 'Disabled'}
                          </Badge>
                        </div>
                        <div className="mt-1 text-sm text-zinc-500">{broker.description || 'No broker description'}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={Boolean(broker.is_active)}
                          disabled={savingBrokerId === broker.id}
                          onCheckedChange={(checked) => handleToggleBrokerActive(broker, checked)}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleSaveBroker(broker)}
                          disabled={savingBrokerId === broker.id}
                        >
                          {savingBrokerId === broker.id ? 'Saving...' : 'Save Broker'}
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {draft.map((entry) => (
                        <Badge key={`${broker.id}-${entry.symbol}`} variant="outline" className="flex items-center gap-2">
                          <span>{entry.display_label || entry.symbol}</span>
                          <button
                            type="button"
                            className="text-zinc-400 hover:text-white"
                            onClick={() => removeBrokerInstrument(broker.id, entry.symbol)}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                      {!draft.length ? (
                        <span className="text-sm text-zinc-500">No instruments configured yet.</span>
                      ) : null}
                    </div>

                    <div className="mt-4 flex flex-col gap-3 md:flex-row">
                      <Input
                        value={brokerInstrumentInputs[broker.id] || ''}
                        onChange={(e) => setBrokerInstrumentInputs((current) => ({ ...current, [broker.id]: e.target.value }))}
                        placeholder="Add symbol (ex: MCL, EURUSD, BTCUSD)"
                        className="bg-zinc-800 border-zinc-700"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => addBrokerInstrument(broker.id, brokerInstrumentInputs[broker.id])}
                      >
                        Add Instrument
                      </Button>
                    </div>

                    {draft.length ? (
                      <div className="mt-4 overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800">
                              <TableHead>Symbol</TableHead>
                              <TableHead>Label</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Symbol ID</TableHead>
                              <TableHead>Contract ID</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {draft.map((entry) => (
                              <TableRow key={`${broker.id}-${entry.symbol}-row`} className="border-zinc-800">
                                <TableCell className="font-medium text-white">{entry.symbol}</TableCell>
                                <TableCell>
                                  <Input
                                    value={entry.display_label || ''}
                                    onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'display_label', e.target.value)}
                                    className="bg-zinc-800 border-zinc-700"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    value={entry.display_name || ''}
                                    onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'display_name', e.target.value)}
                                    className="bg-zinc-800 border-zinc-700"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Select
                                    value={entry.instrument_type || 'futures'}
                                    onValueChange={(value) => updateBrokerInstrumentField(broker.id, entry.symbol, 'instrument_type', value)}
                                  >
                                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                      <SelectItem value="futures">futures</SelectItem>
                                      <SelectItem value="forex">forex</SelectItem>
                                      <SelectItem value="crypto">crypto</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                <TableCell>
                                  <Input
                                    value={entry.symbol_id || ''}
                                    onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'symbol_id', e.target.value)}
                                    className="bg-zinc-800 border-zinc-700"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    value={entry.contract_id || ''}
                                    onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'contract_id', e.target.value)}
                                    className="bg-zinc-800 border-zinc-700"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Market Session Policies</CardTitle>
          <CardDescription>
            JSON source of truth for symbol aliases, broker overrides, and market-session templates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            value={marketSessionConfigDraft}
            onChange={(e) => setMarketSessionConfigDraft(e.target.value)}
            className="min-h-[360px] w-full rounded-md border border-zinc-700 bg-zinc-950 p-3 font-mono text-sm text-white"
            placeholder={`{\n  "policies": {}\n}`}
          />
          <div className="flex justify-end">
            <Button onClick={handleSaveMarketSessionConfig} disabled={savingMarketSessionConfig}>
              {savingMarketSessionConfig ? 'Saving...' : 'Save Session Config'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrokersTab;
